import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ButtonVariants, Table } from '@carboledger/ui';
import { Plus } from '@carboledger/ui/icons';
import { ROUTES } from '@/app/routes';
import { ConfirmDialog } from '@/components/feedback';
import { buildTableLabels } from '@/config/tableLabels';
import { useTableState, useTranslations } from '@/hooks';
import { EMPTY_LIST } from '@/lib/utils';
import emptyBoxIllustration from '@/assets/illustrations/empty-box.png';
import { useDeleteCustomerSiteMutation, useGetCustomerSitesQuery } from '../api/customersApi';
import { buildCustomerSitesColumns } from '../constants/customerSitesColumns';

const getSiteRowId = (row) => row.id;

/**
 * Props:
 *   customer  the customer detail view model — its id scopes the table
 *   onAddSites  opens the full-page add-sites form
 */
export const CustomerSitesTabView = ({ customer, onAddSites }) => {
  const t = useTranslations();
  const navigate = useNavigate();
  const table = useTableState();
  const [siteToDelete, setSiteToDelete] = useState(null);

  const queryArg = useMemo(
    () => ({ customerId: customer.id, params: table.queryParams }),
    [customer.id, table.queryParams],
  );
  const { data, isFetching } = useGetCustomerSitesQuery(queryArg);
  const [deleteSite] = useDeleteCustomerSiteMutation();
  const rows = data?.rows ?? EMPTY_LIST;

  // Ids with a DELETE in flight — gates the double-click RTK Query won't dedupe.
  const deletingIdsRef = useRef(new Set());

  const handleEdit = useCallback(
    (row) => navigate(ROUTES.customerSiteEdit(customer.id, row.id)),
    [navigate, customer.id],
  );
  const handleDelete = useCallback((row) => setSiteToDelete(row), []);
  const handleCancelDelete = useCallback(() => setSiteToDelete(null), []);

  const handleConfirmDelete = useCallback(async () => {
    const id = siteToDelete?.id;
    if (id == null || deletingIdsRef.current.has(id)) return;
    deletingIdsRef.current.add(id);
    try {
      await deleteSite({ customerId: customer.id, siteId: id }).unwrap();
      setSiteToDelete(null);
    } catch {
      // notifyApiErrors already surfaced it.
    } finally {
      deletingIdsRef.current.delete(id);
    }
  }, [deleteSite, customer.id, siteToDelete]);

  const handleSortChange = useCallback(
    (next) => {
      table.setSort(next ? { field: next.id, direction: next.dir } : null);
      table.setPage(1);
    },
    [table],
  );

  const handleSearchChange = useCallback(
    (value) => {
      table.setSearch(value);
      table.setPage(1);
    },
    [table],
  );

  const columns = useMemo(
    () =>
      buildCustomerSitesColumns(t, {
        serialOffset: (table.page - 1) * table.pageSize,
        onEdit: handleEdit,
        onDelete: handleDelete,
      }),
    [t, table.page, table.pageSize, handleEdit, handleDelete],
  );

  const toolbarActions = useMemo(
    () => [
      {
        id: 'add-site',
        label: t('CUSTOMERS_ACTION_ADD_SITES'),
        icon: Plus,
        variant: ButtonVariants.primary,
        onClick: onAddSites,
      },
    ],
    [t, onAddSites],
  );

  const emptyState = useMemo(
    () => ({
      image: <img src={emptyBoxIllustration} alt="" width={200} height={200} />,
      title: t('CUSTOMER_SITES_EMPTY_TITLE'),
      description: t('CUSTOMER_SITES_EMPTY_DESCRIPTION'),
      primaryLabel: t('CUSTOMERS_ACTION_ADD_SITES'),
      onPrimaryClick: onAddSites,
    }),
    [t, onAddSites],
  );

  const noResults = useMemo(
    () => ({
      title: t('CUSTOMER_SITES_NO_RESULTS_TITLE'),
      description: t('COMMON_NO_RESULTS_DESCRIPTION'),
    }),
    [t],
  );

  const tableLabels = useMemo(() => buildTableLabels(t), [t]);

  return (
    <>
      <Table
        columns={columns}
        data={rows}
        total={data?.totalCount ?? 0}
        loading={isFetching}
        getRowId={getSiteRowId}
        searchable
        onSortChange={handleSortChange}
        onSearchChange={handleSearchChange}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        toolbarActions={toolbarActions}
        emptyState={emptyState}
        noResults={noResults}
        labels={tableLabels}
      />

      <ConfirmDialog
        open={siteToDelete !== null}
        title={t('CUSTOMER_SITES_DELETE_TITLE')}
        bodyTitle={t('CUSTOMER_SITES_DELETE_BODY_TITLE', { siteName: siteToDelete?.shippingTo })}
        description={t('CUSTOMER_SITES_DELETE_DESCRIPTION', {
          siteName: siteToDelete?.shippingTo,
          customerName: customer.customerName,
        })}
        confirmLabel={t('COMMON_DELETE')}
        tone="danger"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </>
  );
};
